/**
 * Module Configuration Registry
 *
 * Central export for all module configurations.
 * Provides utilities to get modules adapted for specific industries.
 */

import { IndustryId } from '../industryTypes'
import {
  ModuleConfig,
  ModuleId,
  ModuleRegistry,
  SubPageConfig,
  ModuleWorkflow,
  INDUSTRY_CATEGORIES,
} from './moduleTypes'

// Import individual module configs
import { commandCenterConfig } from './command-center'
import { assetIntelligenceConfig } from './asset-intelligence'
import { predictiveOpsConfig } from './predictive-ops'
import { compareConfig } from './compare'
import { rootCauseConfig } from './root-cause'
import { maintenanceConfig } from './maintenance'
import { selfLearningConfig } from './self-learning'
import { executiveConfig } from './executive'

// =============================================================================
// MODULE REGISTRY
// =============================================================================

export const moduleConfigs: Record<ModuleId, ModuleConfig> = {
  'command-center': commandCenterConfig,
  'asset-intelligence': assetIntelligenceConfig,
  'predictive-ops': predictiveOpsConfig,
  'compare': compareConfig,
  'root-cause': rootCauseConfig,
  'maintenance': maintenanceConfig,
  'self-learning': selfLearningConfig,
  'executive': executiveConfig,
}

// =============================================================================
// REGISTRY IMPLEMENTATION
// =============================================================================

/**
 * Get a module configuration by ID
 */
export function getModule(moduleId: ModuleId): ModuleConfig | undefined {
  return moduleConfigs[moduleId]
}

/**
 * Get all modules enabled for a specific industry
 */
export function getModulesForIndustry(industryId: IndustryId): ModuleConfig[] {
  return Object.values(moduleConfigs).filter((module) =>
    module.enabledIndustries.includes(industryId)
  )
}

/**
 * Get the industry-adapted name for a module
 */
export function getModuleName(moduleId: ModuleId, industryId: IndustryId): string {
  const module = moduleConfigs[moduleId]
  if (!module) return moduleId

  const override = module.industryOverrides?.[industryId]
  return override?.name || module.name
}

/**
 * Get sub-pages for a module, filtered and adapted for an industry
 */
export function getSubPagesForModule(
  moduleId: ModuleId,
  industryId: IndustryId
): SubPageConfig[] {
  const module = moduleConfigs[moduleId]
  if (!module) return []

  const override = module.industryOverrides?.[industryId]

  // Start with base sub-pages
  let subPages = [...module.subPages]

  // Add industry-specific additional sub-pages
  if (override?.additionalSubPages) {
    subPages = [...subPages, ...override.additionalSubPages]
  }

  // Filter out hidden sub-pages for this industry
  if (override?.hiddenSubPages) {
    subPages = subPages.filter((sp) => !override.hiddenSubPages!.includes(sp.id))
  }

  // Filter by enabled industries
  subPages = subPages.filter((sp) => {
    if (!sp.enabledIndustries) return true
    return sp.enabledIndustries.includes(industryId)
  })

  // Sort by position
  subPages.sort((a, b) => a.position - b.position)

  return subPages
}

/**
 * Get the industry-adapted label for a sub-page
 */
export function getSubPageLabel(
  moduleId: ModuleId,
  subPageId: string,
  industryId: IndustryId
): string {
  const module = moduleConfigs[moduleId]
  if (!module) return subPageId

  const subPage = module.subPages.find((sp) => sp.id === subPageId)
  if (!subPage) return subPageId

  // Check for industry-specific label
  const industryLabel = subPage.industryLabels?.[industryId]
  if (industryLabel) return industryLabel

  return subPage.name
}

/**
 * Get workflows for a module, adapted for an industry
 */
export function getWorkflowsForModule(
  moduleId: ModuleId,
  industryId: IndustryId
): ModuleWorkflow[] {
  const module = moduleConfigs[moduleId]
  if (!module) return []

  const override = module.industryOverrides?.[industryId]

  // Start with base workflows
  let workflows = [...module.workflows]

  // Add industry-specific workflows
  if (override?.additionalWorkflows) {
    workflows = [...workflows, ...override.additionalWorkflows]
  }

  // Apply industry-specific workflow overrides
  workflows = workflows.map((workflow) => {
    const workflowOverride = workflow.industryOverrides?.[industryId]
    if (workflowOverride) {
      return {
        ...workflow,
        ...workflowOverride,
        steps: workflowOverride.steps || workflow.steps,
      }
    }
    return workflow
  })

  return workflows
}

/**
 * Check if a module is enabled for an industry
 */
export function isModuleEnabled(moduleId: ModuleId, industryId: IndustryId): boolean {
  const module = moduleConfigs[moduleId]
  if (!module) return false
  return module.enabledIndustries.includes(industryId)
}

/**
 * Check if a sub-page is enabled for an industry
 */
export function isSubPageEnabled(
  moduleId: ModuleId,
  subPageId: string,
  industryId: IndustryId
): boolean {
  const subPages = getSubPagesForModule(moduleId, industryId)
  return subPages.some((sp) => sp.id === subPageId)
}

/**
 * Get the full path for a sub-page
 */
export function getSubPagePath(
  moduleId: ModuleId,
  subPageId: string,
  params?: Record<string, string>
): string {
  const module = moduleConfigs[moduleId]
  if (!module) return '/'

  const subPage = module.subPages.find((sp) => sp.id === subPageId)
  if (!subPage) return module.basePath

  let path = `${module.basePath}/${subPage.path}`.replace(/\/+/g, '/')

  // Replace params
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      path = path.replace(`:${key}`, value)
    })
  }

  return path
}

/**
 * Get all module IDs
 */
export function getAllModuleIds(): ModuleId[] {
  return Object.keys(moduleConfigs) as ModuleId[]
}

/**
 * Get module navigation items for an industry
 */
export function getModuleNavItems(industryId: IndustryId) {
  const modules = getModulesForIndustry(industryId)

  return modules.map((module) => ({
    id: module.id,
    name: getModuleName(module.id, industryId),
    icon: module.industryOverrides?.[industryId]?.icon || module.icon,
    path: module.basePath,
    enabled: true,
    subPages: getSubPagesForModule(module.id, industryId).map((sp) => ({
      id: sp.id,
      name: getSubPageLabel(module.id, sp.id, industryId),
      icon: sp.icon,
      path: `${module.basePath}/${sp.path}`.replace(/\/+$/, ''),
      enabled: true,
    })),
  }))
}

// =============================================================================
// MODULE REGISTRY OBJECT
// =============================================================================

export const moduleRegistry: ModuleRegistry = {
  modules: moduleConfigs,
  getModule,
  getModulesForIndustry,
  getSubPagesForModule,
  getWorkflowsForModule,
}

// =============================================================================
// RE-EXPORTS
// =============================================================================

export * from './moduleTypes'
export { commandCenterConfig } from './command-center'
export { assetIntelligenceConfig } from './asset-intelligence'
export { predictiveOpsConfig } from './predictive-ops'
export { compareConfig } from './compare'
export { rootCauseConfig } from './root-cause'
export { maintenanceConfig } from './maintenance'
export { selfLearningConfig } from './self-learning'
export { executiveConfig } from './executive'
